import React from 'react';

import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import Button from '@/components/ui/buttons/Button';
import useModal from '@/hooks/common/useModal';
import useCurrentUser from '@/hooks/store/useCurrentUser';
import { type ParentWithStudents } from '@/interfaces/parent';
import { type RelatedParent } from '@/interfaces/student';

import styles from './ParentProfileV2.module.scss';
import StudentCard from './StudentCard';
interface Props {
  parent: ParentWithStudents;
}

const sortParentsSelfFirst = (
  parents: RelatedParent[],
  currentUserId: number | undefined,
): RelatedParent[] => {
  const self = parents.find((p) => p.id === currentUserId);
  const others = parents
    .filter((p) => p.id !== currentUserId)
    .sort((a, b) => a.created_ts - b.created_ts);
  return self ? [self, ...others] : others;
};

const ParentProfileV2: React.FC<Props> = ({ parent }) => {
  const { showModal } = useModal();
  const [currentUser] = useCurrentUser();

  const handleEditClick =
    (redirectPath: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      showModal('ProfileEditPasswordConfirmModal', { redirectPath });
    };

  return (
    <>
      <section className={styles.ParentProfileV2__Section}>
        <h3 className={styles.ParentProfileV2__SectionTitleParentInfo}>
          保護者情報
        </h3>
        <div className={styles.ParentProfileV2__ParentInfoList}>
          <div className={styles.ParentProfileV2__ListItem}>
            <div className={styles.ParentProfileV2__ListItemContent}>
              <div className={styles.ParentProfileV2__Label}>
                <FormattedMessage id="mypage.profilePage.labels.name" />
              </div>
              <div className={styles.ParentProfileV2__Value}>
                {`${parent.last_name} ${parent.first_name}`}
                <br />
                {`${parent.last_name_kana} ${parent.first_name_kana}`}
              </div>
            </div>
            <Link
              to="/mypage/profile/name/change"
              className={styles.ParentProfileV2__EditLink}
              onClick={handleEditClick('/mypage/profile/name/change')}
            >
              編集
            </Link>
          </div>
          <div className={styles.ParentProfileV2__ListItem}>
            <div className={styles.ParentProfileV2__ListItemContent}>
              <div className={styles.ParentProfileV2__Label}>
                <FormattedMessage id="mypage.profilePage.labels.email" />
              </div>
              <div className={styles.ParentProfileV2__Value}>
                {parent.email}
              </div>
            </div>
            <Link
              to="/mypage/profile/email/change"
              className={styles.ParentProfileV2__EditLink}
              onClick={handleEditClick('/mypage/profile/email/change')}
            >
              編集
            </Link>
          </div>
          <div className={styles.ParentProfileV2__ListItem}>
            <div className={styles.ParentProfileV2__ListItemContent}>
              <div className={styles.ParentProfileV2__Label}>
                <FormattedMessage id="mypage.profilePage.labels.password" />
              </div>
              <div className={styles.ParentProfileV2__Value}>
                <FormattedMessage id="mypage.profilePage.values.dummyPassword" />
              </div>
            </div>
            <Link
              to="/mypage/profile/password/change"
              className={styles.ParentProfileV2__EditLink}
              onClick={handleEditClick('/mypage/profile/password/change')}
            >
              編集
            </Link>
          </div>
        </div>
      </section>

      <section className={styles.ParentProfileV2__Section}>
        <h3 className={styles.ParentProfileV2__SectionTitleStudentInfo}>
          生徒情報
        </h3>
        <div className={styles.ParentProfileV2__StudentCardsContainer}>
          {parent.organization_memberships.length === 0 && (
            <div className={styles.ParentProfileV2__StudentCardsEmpty}>
              現在紐付いている生徒はいません
            </div>
          )}
          {parent.organization_memberships.map((student) => (
            <StudentCard
              key={student.id}
              student={student}
              parents={sortParentsSelfFirst(
                student.parents ?? [],
                currentUser?.id,
              )}
              currentUserId={currentUser?.id}
            />
          ))}
        </div>
        <div className={styles.ParentProfileV2__AddStudentButtonWrapper}>
          <Button
            tag={Link}
            to="/mypage/organization_memberships/new"
            size="m"
            color="primary"
          >
            <FormattedMessage id="mypage.profilePage.addStudent" />
          </Button>
        </div>
      </section>
    </>
  );
};

export default ParentProfileV2;
